import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Zap } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Sign in failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Colourful hero */}
      <div className="hidden lg:block relative">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1550029402-226115b7c579?crop=entropy&cs=tinysrgb&fm=jpg&q=80&w=1600"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/30 via-transparent to-cyan-400/30 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute top-6 left-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-amber-950 px-2.5 py-1 text-[11px] font-bold tracking-wide" data-testid="beta-badge">
            BETA · TEST USERS ONLY
          </span>
        </div>
        <div className="absolute bottom-10 left-10 right-10">
          <div className="flex items-center gap-2 text-white/90">
            <div className="w-8 h-8 rounded-md bg-white/20 backdrop-blur grid place-items-center">
              <Zap className="w-4 h-4" />
            </div>
            <div className="font-display text-sm tracking-tight">PostQik</div>
          </div>
          <h1 className="mt-6 font-display font-black tracking-tight text-4xl sm:text-5xl text-white max-w-md drop-shadow-lg">
            Drive to TikTok, in one tap.
          </h1>
          <p className="mt-3 text-white/90 max-w-md drop-shadow">
            Browse your videos, pick a folder, drop it straight into your TikTok inbox as a draft.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-8 relative">
        <div className="absolute top-4 right-4 lg:hidden">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-amber-950 px-2.5 py-1 text-[10px] font-bold tracking-wide" data-testid="beta-badge-mobile">
            BETA
          </span>
        </div>
        <Card className="w-full max-w-md p-6 sm:p-8 border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-cyan-500 text-white grid place-items-center">
              <Zap className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-black text-lg text-slate-900 tracking-tight">PostQik</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Sign in</div>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            Welcome back — sign in to send your Drive videos to TikTok.
          </p>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-700 hover:to-cyan-700 text-white h-11"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-5 text-center text-sm text-slate-600">
            No account yet?{" "}
            <Link to="/register" className="font-medium text-fuchsia-700 hover:underline" data-testid="register-link">
              Request access
            </Link>
          </div>
          <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-center gap-4 text-[11px] text-slate-500">
            <Link to="/privacy" className="hover:text-slate-900" data-testid="login-privacy-link">
              Privacy Policy
            </Link>
            <span aria-hidden="true">•</span>
            <Link to="/tos" className="hover:text-slate-900" data-testid="login-tos-link">
              Terms of Service
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
